import { type Connection, PublicKey } from '@solana/web3.js';
import { estimateComputeUnits } from '../constants/cu-table.js';
import {
  ASSOCIATED_TOKEN_PROGRAM_ID,
  MORFI_OPINION_PROGRAM_ID,
  TOKEN_PROGRAM_ID,
} from '../constants/programs.js';
import { getProgram } from '../idl/loader.js';
import { deriveUsdcAta } from '../pda/ata.js';
import type { BuildResult } from '../types/build-result.js';
import type { BuyParams, SellParams, SwapParams } from '../types/trading.js';
import {
  createBuildResult,
  createIdempotentAtaIx,
  invokeMethod,
  resolveReferralAccounts,
} from './common.js';

type MarketAccountV2 = {
  config: PublicKey;
  nOutcomes: number;
  outcomeMints: Array<PublicKey>;
};

type MarketConfigAccountV2 = {
  backerFeeShareConfig: PublicKey;
  quoteMint: PublicKey;
};

type V2AccountNamespace<T> = {
  fetch(address: PublicKey): Promise<T>;
};

type V2ProgramAccounts = {
  account: {
    market: V2AccountNamespace<MarketAccountV2>;
    marketConfig: V2AccountNamespace<MarketConfigAccountV2>;
  };
};

type TradeAccountsV2 = {
  marketConfig: PublicKey;
  quoteMint: PublicKey;
  backerFeeShareConfig: PublicKey;
  bondingCurve: PublicKey;
  bcQuoteVault: PublicKey;
  bcTokenVault: PublicKey;
  outcomeMint: PublicKey;
  claimRightsMint: PublicKey;
  traderClaimRightsAta: PublicKey;
  traderOutcomeAta: PublicKey;
  backerFeeVault: PublicKey;
  platformFeeVault: PublicKey;
  prizePoolVault: PublicKey;
};

const BONDING_CURVE_SEED: Buffer = Buffer.from('bc');
const VAULT_SEED: Buffer = Buffer.from('vault');
const BC_QUOTE_VAULT_SEED: Buffer = Buffer.from('bc_quote');
const BACKER_FEE_VAULT_SEED: Buffer = Buffer.from('backer_fee');
const PLATFORM_FEE_VAULT_SEED: Buffer = Buffer.from('platform_fee_vault');
const PRIZE_VAULT_SEED: Buffer = Buffer.from('prize');
const CLAIM_RIGHTS_MINT_SEED: Buffer = Buffer.from('claim_rights_mint');
const EVENT_AUTHORITY_SEED: Buffer = Buffer.from('__event_authority');

function u8Seed(value: number): Buffer {
  if (!Number.isInteger(value) || value < 0 || value > 255) {
    throw new Error(`outcome index ${value} outside u8 range`);
  }
  return Buffer.from([value]);
}

function derivePda(seeds: Array<Buffer | Uint8Array>, programId: PublicKey): PublicKey {
  return PublicKey.findProgramAddressSync(seeds, programId)[0];
}

function deriveAssociatedTokenAddress(
  owner: PublicKey,
  mint: PublicKey,
  tokenProgram: PublicKey = TOKEN_PROGRAM_ID,
): PublicKey {
  return PublicKey.findProgramAddressSync(
    [owner.toBuffer(), tokenProgram.toBuffer(), mint.toBuffer()],
    ASSOCIATED_TOKEN_PROGRAM_ID,
  )[0];
}

function tradeSignerKeys(user: PublicKey, ataPayer: PublicKey): Array<PublicKey> {
  if (ataPayer.equals(user)) {
    return [user];
  }
  return [user, ataPayer];
}

function deriveBondingCurvePda(
  market: PublicKey,
  outcomeIndex: number,
  programId: PublicKey,
): PublicKey {
  return derivePda([BONDING_CURVE_SEED, market.toBuffer(), u8Seed(outcomeIndex)], programId);
}

function deriveBcQuoteVaultPda(
  market: PublicKey,
  outcomeIndex: number,
  programId: PublicKey,
): PublicKey {
  return derivePda(
    [VAULT_SEED, BC_QUOTE_VAULT_SEED, market.toBuffer(), u8Seed(outcomeIndex)],
    programId,
  );
}

function deriveClaimRightsMintPda(
  market: PublicKey,
  outcomeIndex: number,
  programId: PublicKey,
): PublicKey {
  return derivePda([CLAIM_RIGHTS_MINT_SEED, market.toBuffer(), u8Seed(outcomeIndex)], programId);
}

function deriveBackerFeeVaultPda(market: PublicKey, programId: PublicKey): PublicKey {
  return derivePda([VAULT_SEED, BACKER_FEE_VAULT_SEED, market.toBuffer()], programId);
}

function derivePlatformFeeVaultPda(marketConfig: PublicKey, programId: PublicKey): PublicKey {
  return derivePda([PLATFORM_FEE_VAULT_SEED, marketConfig.toBuffer()], programId);
}

function derivePrizeVaultPda(market: PublicKey, programId: PublicKey): PublicKey {
  return derivePda([VAULT_SEED, PRIZE_VAULT_SEED, market.toBuffer()], programId);
}

function deriveEventAuthorityPda(programId: PublicKey): PublicKey {
  return derivePda([EVENT_AUTHORITY_SEED], programId);
}

async function loadTradeAccountsV2(
  // ALLOW-UNKNOWN: Anchor `Program<Idl>` is passed in opaquely and narrowed via `V2ProgramAccounts` cast below.
  program: unknown,
  market: PublicKey,
  trader: PublicKey,
  outcomeIndex: number,
  programId: PublicKey,
  collateralMintOverride?: PublicKey,
): Promise<TradeAccountsV2> {
  const accounts = (program as V2ProgramAccounts).account;
  const marketAccount = await accounts.market.fetch(market);
  if (outcomeIndex < 0 || outcomeIndex >= marketAccount.nOutcomes) {
    throw new Error(
      `outcomeIndex ${outcomeIndex} outside market outcome count ${marketAccount.nOutcomes}`,
    );
  }

  const marketConfigAccount = await accounts.marketConfig.fetch(marketAccount.config);
  const quoteMint = collateralMintOverride ?? marketConfigAccount.quoteMint;
  if (collateralMintOverride && !collateralMintOverride.equals(marketConfigAccount.quoteMint)) {
    throw new Error('collateralMint does not match market_config.quote_mint');
  }

  const outcomeMint = marketAccount.outcomeMints[outcomeIndex];
  if (!outcomeMint) {
    throw new Error(`missing outcome mint for outcomeIndex ${outcomeIndex}`);
  }

  const claimRightsMint = deriveClaimRightsMintPda(market, outcomeIndex, programId);
  const bondingCurve = deriveBondingCurvePda(market, outcomeIndex, programId);
  const bcQuoteVault = deriveBcQuoteVaultPda(market, outcomeIndex, programId);
  const bcTokenVault = deriveAssociatedTokenAddress(market, outcomeMint);

  return {
    marketConfig: marketAccount.config,
    quoteMint,
    backerFeeShareConfig: marketConfigAccount.backerFeeShareConfig,
    bondingCurve,
    bcQuoteVault,
    bcTokenVault,
    outcomeMint,
    claimRightsMint,
    traderClaimRightsAta: deriveAssociatedTokenAddress(trader, claimRightsMint),
    traderOutcomeAta: deriveAssociatedTokenAddress(trader, outcomeMint),
    backerFeeVault: deriveBackerFeeVaultPda(market, programId),
    platformFeeVault: derivePlatformFeeVaultPda(marketAccount.config, programId),
    prizePoolVault: derivePrizeVaultPda(market, programId),
  };
}

/**
 * Build a buy transaction for purchasing outcome tokens with USDC.
 *
 * Account names and instruction params match the IDL exactly (camelCase conversion
 * of the Rust snake_case fields). See contracts/idl/morfi_opinion_devnet.json.
 *
 * @param connection - Solana RPC connection.
 * @param params - Buy parameters. See {@link BuyParams}.
 * @returns BuildResult with unsigned transaction ready for signing.
 */
export async function buildBuy(connection: Connection, params: BuyParams): Promise<BuildResult> {
  const programId = params.programId ?? MORFI_OPINION_PROGRAM_ID;
  const program = await getProgram(connection, programId);
  const tradeAccounts = await loadTradeAccountsV2(
    program,
    params.market,
    params.user,
    params.outcomeIndex,
    programId,
    params.collateralMint,
  );
  if (params.recipient && !params.recipient.equals(params.user)) {
    throw new Error('contracts buy does not support recipient != user');
  }
  const userQuoteAta = deriveUsdcAta(params.user, tradeAccounts.quoteMint);

  // Pre-instructions for ATA creation (payer funds rent; defaults to user)
  const ataPayer = params.payer ?? params.user;
  const preInstructions = [
    createIdempotentAtaIx(ataPayer, params.user, tradeAccounts.quoteMint, TOKEN_PROGRAM_ID),
    createIdempotentAtaIx(ataPayer, params.user, tradeAccounts.outcomeMint, TOKEN_PROGRAM_ID),
    createIdempotentAtaIx(ataPayer, params.user, tradeAccounts.claimRightsMint, TOKEN_PROGRAM_ID),
  ];

  // Determine CU — default or graduation tier
  const cuOverride = params.computeUnits;
  const computeUnits = cuOverride ?? estimateComputeUnits('buy', 'default');

  // Referral accounts (reserved for future remaining_accounts support)
  resolveReferralAccounts(params.referrer, programId);

  const ix = await invokeMethod(
    program,
    'buy',
    params.outcomeIndex,
    params.amountIn,
    params.minTokensOut,
  )
    .accounts({
      market: params.market,
      marketConfig: tradeAccounts.marketConfig,
      backerFeeShareConfig: tradeAccounts.backerFeeShareConfig,
      bondingCurve: tradeAccounts.bondingCurve,
      bcQuoteVault: tradeAccounts.bcQuoteVault,
      bcTokenVault: tradeAccounts.bcTokenVault,
      outcomeMint: tradeAccounts.outcomeMint,
      claimRightsMint: tradeAccounts.claimRightsMint,
      traderClaimRightsAta: tradeAccounts.traderClaimRightsAta,
      traderQuoteAta: userQuoteAta,
      traderOutcomeAta: tradeAccounts.traderOutcomeAta,
      backerFeeVault: tradeAccounts.backerFeeVault,
      platformFeeVault: tradeAccounts.platformFeeVault,
      prizePoolVault: tradeAccounts.prizePoolVault,
      trader: params.user,
      tokenProgram: TOKEN_PROGRAM_ID,
      eventAuthority: deriveEventAuthorityPda(programId),
      program: programId,
    })
    .instruction();

  const allInstructions = [...preInstructions, ix];
  return createBuildResult(allInstructions, tradeSignerKeys(params.user, ataPayer), computeUnits);
}

/**
 * Build a sell transaction for selling outcome tokens back to USDC.
 *
 * Account names and instruction params match the IDL exactly.
 *
 * @param connection - Solana RPC connection.
 * @param params - Sell parameters. See {@link SellParams}.
 * @returns BuildResult with unsigned transaction ready for signing.
 */
export async function buildSell(connection: Connection, params: SellParams): Promise<BuildResult> {
  const programId = params.programId ?? MORFI_OPINION_PROGRAM_ID;
  const program = await getProgram(connection, programId);
  const tradeAccounts = await loadTradeAccountsV2(
    program,
    params.market,
    params.user,
    params.outcomeIndex,
    programId,
    params.collateralMint,
  );
  const userQuoteAta = deriveUsdcAta(params.user, tradeAccounts.quoteMint);
  const userTokenAta = deriveAssociatedTokenAddress(params.user, tradeAccounts.outcomeMint);
  const userClaimRightsAta = deriveAssociatedTokenAddress(
    params.user,
    tradeAccounts.claimRightsMint,
  );
  const ataPayer = params.payer ?? params.user;
  const preInstructions = [
    createIdempotentAtaIx(ataPayer, params.user, tradeAccounts.quoteMint, TOKEN_PROGRAM_ID),
  ];

  const computeUnits = params.computeUnits ?? estimateComputeUnits('sell', 'default');

  const ix = await invokeMethod(
    program,
    'sell',
    params.outcomeIndex,
    params.tokensIn,
    params.minUsdcOut,
  )
    .accounts({
      market: params.market,
      marketConfig: tradeAccounts.marketConfig,
      backerFeeShareConfig: tradeAccounts.backerFeeShareConfig,
      bondingCurve: tradeAccounts.bondingCurve,
      bcQuoteVault: tradeAccounts.bcQuoteVault,
      bcTokenVault: tradeAccounts.bcTokenVault,
      outcomeMint: tradeAccounts.outcomeMint,
      claimRightsMint: tradeAccounts.claimRightsMint,
      traderClaimRightsAta: userClaimRightsAta,
      traderQuoteAta: userQuoteAta,
      traderOutcomeAta: userTokenAta,
      backerFeeVault: tradeAccounts.backerFeeVault,
      platformFeeVault: tradeAccounts.platformFeeVault,
      prizePoolVault: tradeAccounts.prizePoolVault,
      trader: params.user,
      tokenProgram: TOKEN_PROGRAM_ID,
      eventAuthority: deriveEventAuthorityPda(programId),
      program: programId,
    })
    .instruction();

  return createBuildResult(
    [...preInstructions, ix],
    tradeSignerKeys(params.user, ataPayer),
    computeUnits,
  );
}

/**
 * Build an outcome→outcome SWAP as TWO instructions in ONE transaction: a
 * `sell` of the `from` outcome followed by a `buy` of the `to` outcome with the
 * proceeds. Atomic (both land or both revert — no partial-fill where the user
 * is left holding USDC) and a single signature/round-trip, while still emitting
 * a distinct sell event and buy event for PnL indexing.
 *
 * Instruction order: ATA-creates (USDC + to-outcome + to-claim-rights) → sell →
 * buy. The USDC ATA is created first so the sell can credit it; the buy then
 * debits it. The buy budget (`buyAmountIn`) is sized from the sell's floored
 * proceeds, so the sell (guarantees ≥ `minUsdcOut`) always covers the buy.
 *
 * NOTE: even with no sibling bonding-curve accounts on either leg, the combined
 * tx still exceeds the 1232-byte packet limit at high outcome counts — the
 * caller MUST submit it through an address-lookup-table path (the Turnkey/relay
 * submit compacts the shared read-only accounts). The two-tx fallback (separate
 * sell then buy) fits without any ALT at every outcome count.
 */
export async function buildSwap(connection: Connection, params: SwapParams): Promise<BuildResult> {
  const programId = params.programId ?? MORFI_OPINION_PROGRAM_ID;
  const program = await getProgram(connection, programId);
  const fromAccts = await loadTradeAccountsV2(
    program,
    params.market,
    params.user,
    params.fromOutcomeIndex,
    programId,
    params.collateralMint,
  );
  const toAccts = await loadTradeAccountsV2(
    program,
    params.market,
    params.user,
    params.toOutcomeIndex,
    programId,
    params.collateralMint,
  );
  const userQuoteAta = deriveUsdcAta(params.user, fromAccts.quoteMint);
  const ataPayer = params.payer ?? params.user;

  // Idempotent ATA creates BEFORE the legs. The USDC ATA must exist so the sell
  // can credit proceeds; the TO-outcome + TO-claim-rights ATAs receive the buy.
  // The FROM-outcome / FROM-claim-rights ATAs already exist (the user holds the
  // from-outcome they are selling).
  const preInstructions = [
    createIdempotentAtaIx(ataPayer, params.user, fromAccts.quoteMint, TOKEN_PROGRAM_ID),
    createIdempotentAtaIx(ataPayer, params.user, toAccts.outcomeMint, TOKEN_PROGRAM_ID),
    createIdempotentAtaIx(ataPayer, params.user, toAccts.claimRightsMint, TOKEN_PROGRAM_ID),
  ];

  const sellIx = await invokeMethod(
    program,
    'sell',
    params.fromOutcomeIndex,
    params.fromTokensIn,
    params.minUsdcOut,
  )
    .accounts({
      market: params.market,
      marketConfig: fromAccts.marketConfig,
      backerFeeShareConfig: fromAccts.backerFeeShareConfig,
      bondingCurve: fromAccts.bondingCurve,
      bcQuoteVault: fromAccts.bcQuoteVault,
      bcTokenVault: fromAccts.bcTokenVault,
      outcomeMint: fromAccts.outcomeMint,
      claimRightsMint: fromAccts.claimRightsMint,
      traderClaimRightsAta: deriveAssociatedTokenAddress(params.user, fromAccts.claimRightsMint),
      traderQuoteAta: userQuoteAta,
      traderOutcomeAta: deriveAssociatedTokenAddress(params.user, fromAccts.outcomeMint),
      backerFeeVault: fromAccts.backerFeeVault,
      platformFeeVault: fromAccts.platformFeeVault,
      prizePoolVault: fromAccts.prizePoolVault,
      trader: params.user,
      tokenProgram: TOKEN_PROGRAM_ID,
      eventAuthority: deriveEventAuthorityPda(programId),
      program: programId,
    })
    .instruction();

  const buyIx = await invokeMethod(
    program,
    'buy',
    params.toOutcomeIndex,
    params.buyAmountIn,
    params.minTokensOut,
  )
    .accounts({
      market: params.market,
      marketConfig: toAccts.marketConfig,
      backerFeeShareConfig: toAccts.backerFeeShareConfig,
      bondingCurve: toAccts.bondingCurve,
      bcQuoteVault: toAccts.bcQuoteVault,
      bcTokenVault: toAccts.bcTokenVault,
      outcomeMint: toAccts.outcomeMint,
      claimRightsMint: toAccts.claimRightsMint,
      traderClaimRightsAta: toAccts.traderClaimRightsAta,
      traderQuoteAta: userQuoteAta,
      traderOutcomeAta: toAccts.traderOutcomeAta,
      backerFeeVault: toAccts.backerFeeVault,
      platformFeeVault: toAccts.platformFeeVault,
      prizePoolVault: toAccts.prizePoolVault,
      trader: params.user,
      tokenProgram: TOKEN_PROGRAM_ID,
      eventAuthority: deriveEventAuthorityPda(programId),
      program: programId,
    })
    .instruction();

  // Summed CU — two bonding-curve trades in one tx.
  const computeUnits =
    params.computeUnits ??
    estimateComputeUnits('sell', 'default') + estimateComputeUnits('buy', 'default');

  return createBuildResult(
    [...preInstructions, sellIx, buyIx],
    tradeSignerKeys(params.user, ataPayer),
    computeUnits,
  );
}
